import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiCopy, FiCheck, FiUsers, FiUserCheck, FiUserPlus, FiSearch } from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import styles from './Team.module.css';
import axiosClient from '../../api/axiosClient';

interface Member {
  id: string;
  full_name: string;
  email: string;
  referral_code: string;
  created_at: string;
  parent_name?: string | null;
  invested?: boolean;
}

const LEVELS = [
  { key: 'level1', title: 'Direct referrals (1st Team)', Icon: FiUserPlus, color: '#1d60f6' },
  { key: 'level2', title: '2nd Team', Icon: FiUserCheck, color: '#7c5cff' },
  { key: 'level3', title: '3rd Team', Icon: FiUsers, color: '#4fae7a' },
  { key: 'level4', title: '4th Team', Icon: FiUsers, color: '#e8a86a' },
  { key: 'level5', title: '5th Team', Icon: FiUsers, color: '#d6657a' },
  { key: 'level6', title: '6th Team', Icon: FiUsers, color: '#58b6c9' },
  { key: 'level7', title: '7th Team', Icon: FiUsers, color: '#b58af0' },
] as const;

const Team: React.FC = () => {
  const [data, setData] = useState<{ referralCode: string | null; level1: Member[]; level2: Member[]; level3: Member[]; level4: Member[]; level5: Member[]; level6: Member[]; level7: Member[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axiosClient.get('/team/mine').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  const referralLink = data?.referralCode ? `${window.location.origin}/register?ref=${data.referralCode}` : '';

  const copyCode = async () => {
    if (!referralLink) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        // Fallback for browsers/contexts where the Clipboard API isn't available (e.g. non-HTTPS).
        const textarea = document.createElement('textarea');
        textarea.value = referralLink;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!ok) throw new Error('execCommand copy failed');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy automatically — please select and copy the link manually.');
    }
  };

  const q = search.trim().toLowerCase();
  const matches = (m: Member) =>
    !q || m.full_name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);

  const renderLevel = (title: string, allMembers: Member[], Icon: React.ElementType, color: string, showParent: boolean) => {
    const members = allMembers.filter(matches);
    return (
    <div className={styles.levelBlock} style={{ ['--accent' as any]: color }}>
      <div className={styles.levelHead}>
        <div className={styles.levelIcon}><Icon /></div>
        <span>{title}</span>
        <span className={styles.levelCount}>{q ? `${members.length} / ${allMembers.length}` : allMembers.length}</span>
      </div>
      {members.length === 0 ? (
        <div className={styles.emptyLevel}>{q ? 'No match in this level.' : 'No members yet.'}</div>
      ) : (
        <div className={styles.memberList}>
          {members.map((m) => (
            <div className={styles.memberRow} key={m.id}>
              <div className={styles.memberAvatar}>{m.full_name?.[0]?.toUpperCase() || '?'}</div>
              <div>
                <div className={styles.memberName}>{m.full_name}</div>
                <div className={styles.memberEmail}>{m.email}</div>
                {showParent && m.parent_name && <div className={styles.memberVia}>via {m.parent_name}</div>}
              </div>
              <span className={`${styles.statusPill} ${m.invested ? styles.statusOn : styles.statusOff}`}>
                {m.invested ? 'Invested' : 'Not invested'}
              </span>
              <div className={styles.memberDate}>{new Date(m.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  const allMembers: Member[] = data ? LEVELS.flatMap((l) => data[l.key] || []) : [];
  const investedCount = allMembers.filter((m) => m.invested).length;

  return (
    <DashboardShell>
      <PageHeader title="My Team" subtitle="Your referral network — your direct referrals are your 1st team, with 7 team levels in total." action={<WalletBadge />} />
      <div className={styles.page}>
        {!loading && data?.referralCode && (
          <div className={styles.codeCard}>
            <div>
              <div className={styles.codeLabel}>Your referral link</div>
              <div className={styles.codeValue}>{referralLink}</div>
              <div className={styles.codeLabel} style={{ marginTop: 6, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                Code: {data.referralCode}
              </div>
            </div>
            <button className={styles.copyBtn} onClick={copyCode}>
              {copied ? <FiCheck /> : <FiCopy />} {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        )}

        {loading && <div className={styles.muted}>Loading…</div>}

        {!loading && data && (
          <>
            <div className={styles.teamTools}>
              <div className={styles.teamSummary}>
                <b>{allMembers.length}</b> team members · <b>{investedCount}</b> invested
              </div>
              <div className={styles.searchBox}>
                <FiSearch />
                <input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            {LEVELS.map((l, i) => (
              <React.Fragment key={l.key}>{renderLevel(l.title, data[l.key] || [], l.Icon, l.color, i > 0)}</React.Fragment>
            ))}
          </>
        )}
      </div>
    </DashboardShell>
  );
};

export default Team;